import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@forbiz/web-kit'

import styles from './Shell.module.css'

/**
 * 기업·업체 담당자 셸. 인증된 사용자만 여기 온다(RequireAuth가 보증).
 * 상단 탭은 매일 쓰는 것만 두고, 관리·설정성(청구·빠른주문설정·내회사)은 아바타
 * 드롭다운으로 접어 상단 과밀을 줄인다. 업체는 배정 주문만 본다.
 */
export function Shell() {
  const { user, logout } = useAuth()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const isAdmin = user?.role === 'COMPANY_ADMIN'

  // 상단 탭: 매일 쓰는 것만. 업체는 배정 주문만.
  const nav = isCompany
    ? [
        { to: '/', label: '홈', end: true },
        { to: '/products', label: '둘러보기', end: false },
        { to: '/orders', label: '주문 내역', end: false },
        { to: '/stats', label: '리포트', end: false },
      ]
    : [
        { to: '/orders', label: '배정 주문', end: false },
        { to: '/settlement', label: '정산', end: false },
      ]

  // 기업 관리자 전용(청구·설정)은 프로필 메뉴로. 권한표 4.1·senderPreset.manage.
  const menuNav = isAdmin
    ? [
        { to: '/settings/products', label: '주문서 노출' },
        { to: '/invoices', label: '청구 내역' },
        { to: '/settings/senders', label: '주문 설정' },
        { to: '/settings/company', label: '내 회사' },
      ]
    : []

  // 전 사용자 공통(소식·이용안내) — 상단 과밀 없이 프로필 메뉴에서 언제든 닿게.
  // 추천(회의 S5)은 기업 사용자 누구나(관리자·담당자) — '내 회사'(관리자 전용)에서 뺐다.
  const helpNav = [
    ...(isCompany ? [{ to: '/referral', label: '추천하고 포인트 받기' }] : []),
    { to: '/notices', label: '소식' },
    { to: '/guide', label: '이용안내' },
  ]

  const initial = user?.name?.trim().charAt(0) || '·'
  const { pathname } = useLocation()

  // 프로필 드롭다운 — 경로 변경·바깥 클릭·Esc로 닫는다.
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => setMenuOpen(false), [pathname])
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <img src="/favicon.png" alt="" />
          </span>
          포비즈
        </div>
        <nav className={styles.nav}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => {
                // '/orders'는 하위 경로에도 매칭된다. 그중 '/orders/new'는 그 자체가 별도
                // 탭('주문하기')이라 '주문 내역'을 활성으로 치지 않는다(둘 다 켜지는 버그 방지).
                const active =
                  isActive && !(item.to === '/orders' && pathname.startsWith('/orders/new'))
                return active ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem
              }}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className={styles.user} ref={menuRef}>
          <button
            type="button"
            className={styles.profileBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className={styles.avatar} aria-hidden="true">
              {initial}
            </span>
            <span className={styles.userName}>{user?.name} 님</span>
            <span className={styles.caret} aria-hidden="true">
              ⌄
            </span>
          </button>
          {menuOpen ? (
            <div className={styles.menu} role="menu">
              {menuNav.map((item) => (
                <NavLink key={item.to} to={item.to} role="menuitem" className={() => styles.menuItem}>
                  {item.label}
                </NavLink>
              ))}
              {menuNav.length > 0 ? <div className={styles.menuDivider} /> : null}
              {helpNav.map((item) => (
                <NavLink key={item.to} to={item.to} role="menuitem" className={() => styles.menuItem}>
                  {item.label}
                </NavLink>
              ))}
              <div className={styles.menuDivider} />
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuLogout}`}
                onClick={() => void logout()}
              >
                로그아웃
              </button>
            </div>
          ) : null}
        </div>
      </header>
      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  )
}
